import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { RootState, ThunkApi } from 'state/store';
import { Dashboard, Status } from 'utils/customTypes';
import { SLICE_STATUS } from 'utils/constants';
import { selectUserId } from 'state/User/userSlice';

/* ============================== STATE SETUP =============================== */
interface AgentDashboardState {
  status: Status;
  hideStatus: Status;
}

const initialState: AgentDashboardState = {
  status: SLICE_STATUS.IDLE,
  hideStatus: SLICE_STATUS.IDLE,
};

const agentDashboardAdapter = createEntityAdapter<Dashboard>();

/* ============================== REDUX THUNK =============================== */
function concurrencyCondition(
  thunkParameters: unknown,
  { getState }: { getState: () => RootState }
): boolean {
  const { status } = getState().normalized.agentDashboard;
  return status !== SLICE_STATUS.LOADING;
}

export const getAgentDashboards = createAsyncThunk<Dashboard[], void, ThunkApi>(
  'normalized/agentDashboard/getAgentDashboards',
  async (_, { getState, extra: { getAgentDashboardApi } }) => {
    const currentUserId = selectUserId(getState());
    return await getAgentDashboardApi().getDashboards(currentUserId);
  },
  { condition: concurrencyCondition }
);

export const hideAgentDashboard = createAsyncThunk<string, string, ThunkApi>(
  'normalized/agentDashboard/hideAgentDashboard',
  async (dashboardId, { extra: { getAgentDashboardApi } }) => {
    await getAgentDashboardApi().setVisibility(dashboardId, false);
    return dashboardId;
  }
);

/* ================================= REDUCER ================================ */
const agentDashboardSlice = createSlice({
  name: 'normalized/agentDashboard',
  initialState: agentDashboardAdapter.getInitialState(initialState),
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAgentDashboards.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(getAgentDashboards.rejected, (state) => {
        state.status = SLICE_STATUS.FAILED;
      })
      .addCase(getAgentDashboards.fulfilled, (state, action) => {
        agentDashboardAdapter.setAll(state, action.payload);
        state.status = SLICE_STATUS.SUCCESS;
      })
      .addCase(hideAgentDashboard.pending, (state) => {
        state.hideStatus = SLICE_STATUS.LOADING;
      })
      .addCase(hideAgentDashboard.rejected, (state) => {
        state.hideStatus = SLICE_STATUS.FAILED;
      })
      .addCase(hideAgentDashboard.fulfilled, (state, action) => {
        agentDashboardAdapter.removeOne(state, action.payload);
        state.hideStatus = SLICE_STATUS.SUCCESS;
      });
  },
});

/* =============================== SELECTORS ================================ */
const defaultSelectors = agentDashboardAdapter.getSelectors<RootState>(
  (state) => state.normalized.agentDashboard
);

const selectStatus = (state: RootState) =>
  state.normalized.agentDashboard.status;

const selectHideStatus = (state: RootState) =>
  state.normalized.agentDashboard.hideStatus;

export const agentDashboardSelectors = {
  ...defaultSelectors,
  selectStatus,
  selectHideStatus,
};

export default agentDashboardSlice.reducer;
